import type {
  IntrinsicSystemProps,
  MotionPropsFor,
  SystemProps,
} from '@hover/blueprint';
import {
  createContext,
  cssVar,
  motion,
  system,
  useBreakpointValue,
} from '@hover/blueprint';
import type { MotionValue } from 'framer-motion';
import {
  easeOut,
  useMotionTemplate,
  useMotionValue,
  useScroll,
  useTransform,
} from 'framer-motion';
import type { ReactNode, RefObject } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { useStickyDepth } from '../hooks';

import { useNavbarScrollBackground, useNavbarScrollValue } from './Navbar';

type SystemMotionProps = MotionPropsFor<SystemProps>;

interface StackingHeaderContext {
  expandedMobile: number;
  expandedDesktop: number;
  collapsedMobile: number;
  collapsedDesktop: number;
  headerHeightMobile: MotionValue<string>;
  headerHeightDesktop: MotionValue<string>;
  stackedHeightMobile: MotionValue<string>;
  stackedHeightDesktop: MotionValue<string>;
  stackerRef: RefObject<HTMLDivElement>;
  stackerHeight: MotionValue<string>;
  stackerBackground: MotionValue<number>;
}

const [Provider, useContext] = createContext<StackingHeaderContext>({
  name: 'StackingHeaderContext',
  strict: true,
});

/**
 * Apply Framer Motion animated style values responsively
 * using CSS variables and a responsive style prop
 *
 * @param name CSS variable name prefix
 * @param mobile motion value to apply on mobile
 * @param desktop motion value to apply on desktop
 *
 * @returns responsive style prop and motion `style` object
 */
const useResponsiveMotionStyle = <T extends {}>(
  name: string,
  mobile: MotionValue<T>,
  desktop: MotionValue<T>,
) => {
  const $mobile = useMemo(() => cssVar(`${name}-mobile`), [name]);
  const $desktop = useMemo(() => cssVar(`${name}-desktop`), [name]);

  return {
    prop: { base: $mobile.reference, tablet: $desktop.reference },
    style: { [$mobile.variable]: mobile, [$desktop.variable]: desktop },
  };
};

/**
 * Get props to apply to the sticky header element
 */
export const useStackingHeader = () => {
  const zIndex = useStickyDepth();
  const { headerHeightMobile, headerHeightDesktop } = useContext();
  const { prop: height, style } = useResponsiveMotionStyle(
    'stacking-header-height',
    headerHeightMobile,
    headerHeightDesktop,
  );

  return {
    height,
    position: 'sticky',
    style,
    top: 0,
    zIndex,
  } satisfies SystemMotionProps;
};

export interface StackingHeaderProps
  extends MotionPropsFor<IntrinsicSystemProps<'div'>> {}

export const StackingHeader = ({ children, ...props }: StackingHeaderProps) => {
  const { expandedDesktop, expandedMobile } = useContext();
  const { style, height, ...rest } = useStackingHeader();

  return (
    <system.div
      {...rest}
      backgroundColor="neutral.0"
      height={{
        base: `calc(env(safe-area-inset-top) + ${expandedMobile}px)`,
        tablet: `${expandedDesktop}px`,
      }}
    >
      <motion.div
        alignItems="center"
        display="flex"
        flexDirection="row"
        {...props}
        bottom={{ base: 0, tablet: 'unset' }}
        height={height}
        left={0}
        marginTop="auto"
        position="absolute"
        right={0}
        style={style}
        top={{ base: 'unset', tablet: 0 }}
      >
        {children}
      </motion.div>
    </system.div>
  );
};

export interface UseStackingHeaderStackerOptions {
  /**
   * Expanded height of stacker element
   */
  expanded: string;
  /**
   * Collapsed height of stacker element
   */
  collapsed: string;
  /**
   * Scroll distance for stacker height transition
   */
  distance?: number;
  /**
   * Padding to apply below the stacker element in the header background
   */
  padding?: string;
  /**
   * Background fade speed multiplier. This controls how fast the stacker
   * background fades in relative to the height transition
   */
  fadeSpeed?: number;
}

/**
 * Get props to apply to an optional "stacker" element that
 * will collapse and stack underneath the sticky header
 */
export const useStackingHeaderStacker = ({
  expanded,
  collapsed,
  distance = 100,
  padding = '0px',
  fadeSpeed = 2,
}: UseStackingHeaderStackerOptions) => {
  const zIndex = useStickyDepth();
  const {
    stackerRef,
    stackerHeight,
    stackerBackground,
    collapsedMobile,
    collapsedDesktop,
  } = useContext();

  const stackerTop = stackerRef?.current?.getBoundingClientRect().top ?? 0;
  const headerHeight = useBreakpointValue(
    {
      base: collapsedMobile,
      tablet: collapsedDesktop,
    },
    { fallback: 'tablet' },
  );

  const offset = stackerTop ? stackerTop - headerHeight : headerHeight;
  const transform = [offset, offset + distance];

  const { scrollY } = useScroll();
  const height = useTransform(scrollY, transform, [expanded, collapsed]);
  const background = useTransform(scrollY, transform, [0, 1]);

  stackerHeight.set(`calc(${height.get()} + ${padding})`);

  useEffect(() => {
    const off = [
      height.on('change', h => stackerHeight.set(`calc(${h} + ${padding})`)),
      background.on('change', b =>
        stackerBackground.set(easeOut(Math.min(b * fadeSpeed, 1))),
      ),
    ];

    return () => off.forEach(o => o());
  }, [
    background,
    fadeSpeed,
    height,
    padding,
    stackerBackground,
    stackerHeight,
  ]);

  const props = {
    position: 'sticky',
    style: { height },
    top: {
      base: `calc(env(safe-area-inset-top) + ${collapsedMobile}px)`,
      tablet: `${collapsedDesktop}px`,
    },
    zIndex,
  } satisfies SystemMotionProps;

  return { ref: stackerRef, ...props };
};

export interface StackingHeaderStackerProps
  extends IntrinsicSystemProps<'div'> {
  options: UseStackingHeaderStackerOptions;
}

export const StackingHeaderStacker = ({
  children,
  options,
  ...props
}: StackingHeaderStackerProps) => {
  const { style, ...rest } = useStackingHeaderStacker(options);

  return (
    <system.div {...props} {...rest} height={options.expanded}>
      <motion.div left={0} position="absolute" right={0} style={style} top={0}>
        {children}
      </motion.div>
    </system.div>
  );
};

export interface UseStackingHeaderStickyOptions {
  /**
   * Margin between header and sticky element
   */
  margin?: string;
}

/**
 * Get props to apply to an element that will stick
 * beneath the header and optional stacker
 */
export const useStackingHeaderSticky = ({
  margin = '0px',
}: UseStackingHeaderStickyOptions = {}) => {
  const zIndex = useStickyDepth();
  const { stackedHeightMobile, stackedHeightDesktop } = useContext();

  const topMobile = useMotionTemplate`calc(${stackedHeightMobile} + ${margin})`;
  const topDesktop = useMotionTemplate`calc(${stackedHeightDesktop} + ${margin})`;

  const { prop: top, style } = useResponsiveMotionStyle(
    'stacking-header-sticky-top',
    topMobile,
    topDesktop,
  );

  return {
    position: 'sticky',
    top,
    style,
    zIndex,
  } satisfies SystemMotionProps;
};

/**
 * Animate a linear gradient with immediate stops to use as a mask to fade in
 * the bottom portion of an element
 *
 * @see {@link https://www.joshwcomeau.com/animation/pride-flags/#drawing-flag-bars-3}
 *
 * @param height height of the top portion of the element that's always visible
 * @param opacity opacity of bottom portion of the element that fades in
 */
const useStackerMask = (
  height: MotionValue<string>,
  opacity: MotionValue<number>,
) =>
  useMotionTemplate`linear-gradient(to bottom, black 0% ${height}, rgba(0, 0, 0, ${opacity}) ${height} 100%)`;

/**
 * Header and stacker background. This should be rendered inside the header
 * element that receives `useStackingHeader` props.
 *
 * @remarks this is necessary so that the blurred background behind the header
 * and stacker element is seamless. Applying blur to two adjacent containers
 * will result in a visible "seam" in the blur effect.
 */
export const StackingHeaderBackground = () => {
  const {
    stackedHeightMobile,
    stackedHeightDesktop,
    headerHeightMobile,
    headerHeightDesktop,
    stackerBackground,
  } = useContext();

  // Use a gradient mask to fade the stacker background in separately
  // from the header once the stacker element starts sticking
  const maskMobile = useStackerMask(headerHeightMobile, stackerBackground);
  const maskDesktop = useStackerMask(headerHeightDesktop, stackerBackground);

  const { prop: height, style: heightStyle } = useResponsiveMotionStyle(
    'stacking-header-background-height',
    stackedHeightMobile,
    stackedHeightDesktop,
  );
  const { prop: maskImage, style: maskStyle } = useResponsiveMotionStyle(
    'stacking-header-background-mask',
    maskMobile,
    maskDesktop,
  );

  const background = useNavbarScrollBackground();

  return (
    <motion.div
      height={height}
      left={0}
      position="fixed"
      right={0}
      style={{
        ...background,
        ...heightStyle,
        ...maskStyle,
      }}
      sx={{ maskImage }}
      top={0}
      zIndex={-1}
    />
  );
};

export interface StackingHeaderProviderProps {
  children?: ReactNode;
  /**
   * Header height to be applied to header element by `useStackingHeader`
   */
  height: {
    mobile: {
      expanded: number;
      collapsed: number;
    };
    desktop: {
      expanded: number;
      collapsed: number;
    };
  };
}

/**
 * Create a sticky header layout with a header element (`useStackingHeader`) that
 * collapses vertically as the page is scrolled and optionally stacks another
 * sticky element (`useStackingHeaderStacker`) on the page beneath the header with
 * a combined "glass" background. Elements can also stick beneath the combined
 * stack with a specified margin (`useStackingHeaderSticky`).
 */
export const StackingHeaderProvider = ({
  children,
  height: {
    mobile: { expanded: expandedMobile, collapsed: collapsedMobile },
    desktop: { expanded: expandedDesktop, collapsed: collapsedDesktop },
  },
}: StackingHeaderProviderProps) => {
  const headerHeightMobile = useNavbarScrollValue(
    `${expandedMobile}px`,
    `${collapsedMobile}px`,
  );
  const headerHeightDesktop = useNavbarScrollValue(
    `${expandedDesktop}px`,
    `${collapsedDesktop}px`,
  );

  const stackerRef = useRef<HTMLDivElement>(null);

  const stackerHeight = useMotionValue('0px');
  const stackerBackground = useMotionValue(0);

  const stackedHeightMobile = useMotionTemplate`calc(${stackerHeight} + ${headerHeightMobile})`;
  const stackedHeightDesktop = useMotionTemplate`calc(${stackerHeight} + ${headerHeightDesktop})`;

  return (
    <Provider
      value={{
        expandedMobile,
        expandedDesktop,
        collapsedMobile,
        collapsedDesktop,
        headerHeightMobile,
        headerHeightDesktop,
        stackedHeightMobile,
        stackedHeightDesktop,
        stackerRef,
        stackerHeight,
        stackerBackground,
      }}
    >
      {children}
    </Provider>
  );
};
